import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import DialogHost from './components/DialogHost';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

// Componentes Homepage
import Header from './components/Secciones/Header';
import Footer from './components/Secciones/Footer';
import SeoManager from './components/SeoManager';
import { UserAuthProvider } from './UserAuthContext';
import { FavoritesProvider } from './FavoritesContext';
import InteractiveBanner from './components/HomePage/InteractiveBanner';
import Containers from './components/HomePage/Containers';
import FeaturesSection from './components/HomePage/FeaturesSection';
import CategoriesSection from './components/HomePage/CategoriesSection';
import ProductShowcase from './components/HomePage/ProductShowcase';
import TopSellers from './components/HomePage/TopSellers';
import RegionalAdvertising from './components/HomePage/RegionalAdvertising';
import { HomeSectionsProvider } from './components/HomePage/HomeSectionsContext';
import FaqSection from './components/HomePage/FaqSection';
import PremiumBenefitsPanel from './components/PremiumBenefitsPanel';
import CookieConsent from './components/CookieConsent';
import MobileLocationPrompt from './components/MobileLocationPrompt';
import LoadingScreen from './components/LoadingScreen';
import QuoteRequestWidget, { isQuoteRequestWidgetVisible } from './components/QuoteRequests/QuoteRequestWidget';
import QuoteProcessInfo from './components/QuoteRequests/QuoteProcessInfo';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import {
  LanguageProvider,
  getLanguageFromPath,
  useLanguage,
  withLanguagePrefix,
} from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';

import RouteGuard from './components/RouteGuard';
import ScrollToTop from './components/ScrollToTop';
import './pages/NotFound.css';

// Administration
import { AdminAuthProvider } from './components/admin/AdminAuthContext';
import AdminProtectedRoute from './components/admin/AdminProtectedRoute';

const Login = lazy(() => import('./pages/LogIn'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const SellerPublicProfile = lazy(() => import('./pages/SellerPublicProfile'));
const SellerReviews = lazy(() => import('./pages/SellerReviews'));
const Account = lazy(() => import('./pages/Account'));
const Finances = lazy(() => import('./pages/Finances'));
const BrowsingHistory = lazy(() => import('./pages/BrowsingHistory'));
const Cart = lazy(() => import('./pages/Cart'));
const Orders = lazy(() => import('./pages/Orders'));
const Checkout = lazy(() => import('./pages/Checkout'));
const About = lazy(() => import('./pages/About'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const PaymentMethods = lazy(() => import('./pages/PaymentMethods'));
const Privacy = lazy(() => import('./pages/Privacy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const Refunds = lazy(() => import('./pages/Refunds'));
const ApiDocumentation = lazy(() => import('./pages/ApiDocumentation'));
const SellLanding = lazy(() => import('./pages/SellLanding'));
const SellProducts = lazy(() => import('./pages/SellProducts'));
const SellRegister = lazy(() => import('./pages/SellRegister'));
const UploadProducts = lazy(() => import('./pages/UploadProducts'));
const BulkUploadProducts = lazy(() => import('./pages/BulkUploadProducts'));
const EditProduct = lazy(() => import('./pages/EditProduct'));
const OrderDetails = lazy(() => import('./pages/OrderDetails'));
const QuoteRequestDetails = lazy(() => import('./pages/QuoteRequestDetails'));
const SellerQuoteDetails = lazy(() => import('./pages/SellerQuoteDetails'));
const SellerOrders = lazy(() => import('./pages/SellerOrders'));
const SellerOrderDetails = lazy(() => import('./pages/SellerOrderDetails'));
const SellerTeamInvitation = lazy(() => import('./pages/SellerTeamInvitation'));
const NotFound = lazy(() => import('./pages/NotFound'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminForgotPassword = lazy(() => import('./pages/admin/AdminForgotPassword'));
const AdminResetPassword = lazy(() => import('./pages/admin/AdminResetPassword'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminOrderDetails = lazy(() => import('./pages/admin/AdminOrderDetails'));
const AdminTickets = lazy(() => import('./pages/admin/AdminTickets'));
const AdminUserDirectory = lazy(() => import('./pages/admin/AdminUserDirectory'));
const AdminComplaints = lazy(() => import('./pages/admin/AdminComplaints'));
const AdminMemberships = lazy(() => import('./pages/admin/AdminMemberships'));
const AdminContent = lazy(() => import('./pages/admin/AdminContent'));
const AdminBanks = lazy(() => import('./pages/admin/AdminBanks'));
const AdminPromotions = lazy(() => import('./pages/admin/AdminPromotions'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminStock = lazy(() => import('./pages/admin/AdminStock'));
const AdminVerification = lazy(() => import('./pages/admin/AdminVerification'));

// Main Page
const Home = () => (
  <div className="flex flex-col min-h-screen">
    <Header />
    <HomeSectionsProvider>
      <main className="flex-grow flex flex-col items-center justify-center">
        <InteractiveBanner />
        <Containers />
        <CategoriesSection />
        <QuoteProcessInfo audience="home" />
        {/* Recommended, Hot Deals, Best Sellers, Just Added and Auctions in one tabbed carousel. */}
        <ProductShowcase />
        <RegionalAdvertising />
        {/* Regional and global rankings with a toggle. */}
        <TopSellers />
        <FeaturesSection />
        {/* <PremiumBenefitsPanel /> */}
        <FaqSection />
      </main>
    </HomeSectionsProvider>
    <Footer />
  </div>
);

// Links

// Reads the localized URL (inside the Router) and hands its language to
// LanguageProvider as the authoritative language for this pageview. See
// LanguageContext.js.
const AppLanguageProvider = ({ children }) => {
  const { pathname } = useLocation();
  return (
    <LanguageProvider urlLanguage={getLanguageFromPath(pathname)}>
      {children}
    </LanguageProvider>
  );
};

const WatchlistRedirect = () => {
  const { language } = useLanguage();
  return <Navigate to={language === 'es' ? '/es/account#watchlist' : '/account#watchlist'} replace />;
};

// Some legacy internal links still point at their unprefixed route. Keep the
// selected language authoritative across those navigations and normalize the
// browser URL so Spanish content always lives under /es.
const LocalizedRouteSynchronizer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();

  React.useEffect(() => {
    if (language !== 'es' || getLanguageFromPath(location.pathname) === 'es') return;
    if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) return;
    const target = `${withLanguagePrefix(location.pathname, 'es')}${location.search}${location.hash}`;
    navigate(target, { replace: true });
  }, [language, location.hash, location.pathname, location.search, navigate]);

  return null;
};

// Every localizable (non-admin) route is mirrored under /es so the Spanish version
// of the site has its own real, crawlable URL instead of sharing one URL whose
// language only changes client-side (localStorage/geolocation). See LanguageContext.js.
const routeDefinitions = [
  { path: '/', element: <Home /> },
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/verify-email', element: <VerifyEmail /> },
  { path: '/search', element: <SearchResults /> },
  { path: '/cart', element: <Cart /> },
  { path: '/about', element: <About /> },
  { path: '/shipping-policy', element: <ShippingPolicy /> },
  { path: '/payment-methods', element: <PaymentMethods /> },
  { path: '/privacy', element: <Privacy /> },
  { path: '/cookies', element: <CookiePolicy /> },
  { path: '/terms', element: <Terms /> },
  { path: '/refunds', element: <Refunds /> },
  { path: '/developers', element: <ApiDocumentation /> },
  { path: '/products/:productid', element: <ProductDetail /> },
  { path: '/sellers/:id', element: <SellerPublicProfile /> },
  { path: '/sellers/:id/reviews', element: <SellerReviews /> },
  { path: '/account', element: <RouteGuard><Account /></RouteGuard> },
  { path: '/finances', element: <RouteGuard><Finances /></RouteGuard> },
  { path: '/browsing-history', element: <RouteGuard><BrowsingHistory /></RouteGuard> },
  { path: '/watchlist', element: <RouteGuard><WatchlistRedirect /></RouteGuard> },
  { path: '/orders', element: <RouteGuard><Orders /></RouteGuard> },
  { path: '/orders/requests/:id', element: <RouteGuard><QuoteRequestDetails /></RouteGuard> },
  { path: '/orders/:id', element: <RouteGuard><OrderDetails /></RouteGuard> },
  { path: '/sell', element: <SellLanding /> },
  { path: '/sell/dashboard', element: <RouteGuard><SellProducts /></RouteGuard> },
  { path: '/sell/team-invitation', element: <SellerTeamInvitation /> },
  { path: '/sell/quotes/:id', element: <RouteGuard><SellerQuoteDetails /></RouteGuard> },
  { path: '/sell/register', element: <RouteGuard><SellRegister /></RouteGuard> },
  { path: '/sell/product', element: <RouteGuard><UploadProducts /></RouteGuard> },
  { path: '/sell/auction', element: <RouteGuard><UploadProducts /></RouteGuard> },
  { path: '/sell/products/bulk', element: <RouteGuard><BulkUploadProducts /></RouteGuard> },
  { path: '/sell/products', element: <RouteGuard><UploadProducts /></RouteGuard> },
  { path: '/sell/product/edit/:productid', element: <RouteGuard><EditProduct /></RouteGuard> },
  { path: '/sell/orders', element: <RouteGuard><SellerOrders /></RouteGuard> },
  { path: '/sell/orders/:id', element: <RouteGuard><SellerOrderDetails /></RouteGuard> },
  { path: '/checkout/:id', element: <RouteGuard><Checkout /></RouteGuard> },
  { path: '/thank-you', element: <RouteGuard><ThankYou /></RouteGuard> },
];

const localizedPath = (path) => (path === '/' ? '/es' : `/es${path}`);

const GlobalQuoteRequestWidget = () => {
  const location = useLocation();
  return isQuoteRequestWidgetVisible(location.pathname) ? <QuoteRequestWidget /> : null;
};

const App = () => {
  return (
    <Router>
      <ScrollToTop />
      <CookieConsentProvider>
      <UserAuthProvider>
        <AppLanguageProvider>
        <LocalizedRouteSynchronizer />
        <SeoManager />
        <CurrencyProvider>
        <FavoritesProvider>
        <AdminAuthProvider>
        <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {routeDefinitions.map(({ path, element }) => (
            <Route key={`en-${path}`} path={path} element={element} />
          ))}
          {routeDefinitions.map(({ path, element }) => (
            <Route key={`es-${path}`} path={localizedPath(path)} element={element} />
          ))}
          <Route path="/sell/es" element={<Navigate to="/es/sell" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/admin/reset-password" element={<AdminResetPassword />} />
          <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="admin-users" element={<AdminProtectedRoute permissions={['administrator']}><AdminUsers /></AdminProtectedRoute>} />
            <Route path="orders" element={<AdminProtectedRoute permissions={['administrator', 'customer_service', 'finance']}><AdminOrders /></AdminProtectedRoute>} />
            <Route path="orders/:id" element={<AdminProtectedRoute permissions={['administrator', 'customer_service', 'finance']}><AdminOrderDetails /></AdminProtectedRoute>} />
            <Route path="tickets" element={<AdminProtectedRoute permissions={['administrator', 'customer_service', 'content_creator']}><AdminTickets /></AdminProtectedRoute>} />
            <Route path="users" element={<AdminProtectedRoute permissions={['administrator', 'customer_service']}><AdminUserDirectory /></AdminProtectedRoute>} />
            <Route path="verification" element={<AdminProtectedRoute permissions={['administrator', 'customer_service']}><AdminVerification /></AdminProtectedRoute>} />
            <Route path="complaints" element={<AdminProtectedRoute permissions={['administrator', 'customer_service']}><AdminComplaints /></AdminProtectedRoute>} />
            <Route path="memberships" element={<AdminProtectedRoute permissions={['administrator', 'customer_service']}><AdminMemberships /></AdminProtectedRoute>} />
            <Route path="reports" element={<AdminProtectedRoute permissions={['administrator', 'customer_service']}><AdminReports /></AdminProtectedRoute>} />
            <Route path="stock" element={<AdminProtectedRoute permissions={['administrator', 'finance']}><AdminStock /></AdminProtectedRoute>} />
            <Route path="content" element={<AdminProtectedRoute permissions={['administrator', 'content_creator']}><AdminContent /></AdminProtectedRoute>} />
            <Route path="banks" element={<AdminProtectedRoute permissions={['administrator', 'finance']}><AdminBanks /></AdminProtectedRoute>} />
            <Route path="promotions" element={<AdminProtectedRoute permissions={['administrator', 'content_creator']}><AdminPromotions /></AdminProtectedRoute>} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <GlobalQuoteRequestWidget />
        <ToastContainer
          position="top-right"
          autoClose={900}
          hideProgressBar
          closeOnClick
          pauseOnHover={false}
          draggable
          newestOnTop
        />
        <DialogHost />
        <CookieConsent />
        <MobileLocationPrompt />
        </AdminAuthProvider>
        </FavoritesProvider>
        </CurrencyProvider>
        </AppLanguageProvider>
      </UserAuthProvider>
      </CookieConsentProvider>
    </Router>
  );
};

export default App;
